import { useState, useEffect } from "react";
import ERPLayout from "@/components/feature/ERPLayout";
import { mockRoles, Role } from "@/mocks/admin";
import { getStoredPermissions, savePermissions, invalidatePermissionsCache, RolePermissionsMap } from "@/hooks/usePagePermission";
import { settingsApi } from "@/services/api";

const allModules = ["Dashboard", "Job Cards", "Clients", "Billing", "Employees", "Reports", "Settings", "Admin Panel", "Deliverables"];
const allPermissions = ["view", "create", "edit", "delete", "export", "upload", "approve"];

const roleColors: Record<string, string> = {
  "Super Admin": "#ef4444",
  "Survey Manager": "#f59e0b",
  "Office Staff": "#3b82f6",
  "Field Surveyor": "#10b981",
  "Lab Analyst": "#8b5cf6",
  "Client Portal User": "#64748b",
};

export default function AdminRolesPage() {
  const [roles, setRoles] = useState<Role[]>(mockRoles);
  const [selectedRole, setSelectedRole] = useState<Role>(mockRoles[0]);
  const [loadingPerms, setLoadingPerms] = useState(true);

  // Load saved permissions from DB on mount and merge into roles
  useEffect(() => {
    setLoadingPerms(true);
    // Force fresh fetch from DB
    invalidatePermissionsCache();
    settingsApi.getRolePermissions()
      .then((data: { permissions?: RolePermissionsMap } | RolePermissionsMap) => {
        const saved: RolePermissionsMap = (data as { permissions?: RolePermissionsMap }).permissions ?? (data as RolePermissionsMap) ?? {};
        if (Object.keys(saved).length > 0) {
          const merged = mockRoles.map((r) => {
            const savedPerms = saved[r.name];
            return savedPerms ? { ...r, permissions: savedPerms } : r;
          });
          setRoles(merged);
          setSelectedRole((prev) => {
            const updated = merged.find((r) => r.id === prev.id);
            return updated ?? prev;
          });
        }
      })
      .catch(() => { /* API offline — use mock defaults */ })
      .finally(() => setLoadingPerms(false));
  }, []);
  const [editMode, setEditMode] = useState(false);
  const [editPerms, setEditPerms] = useState<Record<string, string[]>>({});
  const [successMsg, setSuccessMsg] = useState("");

  // Keep selectedRole in sync when roles state changes
  useEffect(() => {
    const updated = roles.find((r) => r.id === selectedRole.id);
    if (updated) setSelectedRole(updated);
  }, [roles]); // eslint-disable-line react-hooks/exhaustive-deps

  const showSuccess = (msg: string) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(""), 3000); };

  const startEdit = () => {
    setEditPerms(JSON.parse(JSON.stringify(selectedRole.permissions)));
    setEditMode(true);
  };

  const togglePerm = (module: string, perm: string) => {
    setEditPerms((prev) => {
      const current = prev[module] || [];
      const updated = current.includes(perm) ? current.filter((p) => p !== perm) : [...current, perm];
      return { ...prev, [module]: updated };
    });
  };

  const savePerms = async () => {
    const updatedRole = { ...selectedRole, permissions: editPerms };
    const updated = roles.map((r) => r.id === selectedRole.id ? updatedRole : r);
    setRoles(updated);
    setSelectedRole(updatedRole);
    setEditMode(false);

    // Build full permissions map from all current roles
    const fullMap: RolePermissionsMap = {};
    updated.forEach((r) => { fullMap[r.name] = r.permissions; });

    try {
      // Save to DB — savePermissions calls settingsApi.saveRolePermissions
      await savePermissions(fullMap);
      showSuccess(`Permissions for "${selectedRole.name}" saved to database & applied`);
    } catch {
      showSuccess(`Permissions applied (will sync to DB when online)`);
    }
  };

  const permColor: Record<string, { color: string; bg: string }> = {
    view: { color: "#3b82f6", bg: "#eff6ff" },
    create: { color: "#10b981", bg: "#ecfdf5" },
    edit: { color: "#f59e0b", bg: "#fffbeb" },
    delete: { color: "#ef4444", bg: "#fef2f2" },
    export: { color: "#8b5cf6", bg: "#f5f3ff" },
    upload: { color: "#10b981", bg: "#ecfdf5" },
    approve: { color: "#f59e0b", bg: "#fffbeb" },
  };

  return (
    <ERPLayout title="Roles & Permissions" subtitle="Define what each role can access and do in the system">
      {successMsg && (
        <div className="fixed top-4 right-4 z-50 text-white text-sm px-4 py-2 rounded-xl font-medium" style={{ background: "#7c3aed" }}>
          <i className="ri-checkbox-circle-line mr-2" />{successMsg}
        </div>
      )}

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Role List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-bold text-gray-900">System Roles</p>
                <p className="text-xs text-gray-500 mt-0.5">{roles.length} roles defined</p>
              </div>
              <div className="divide-y divide-gray-50">
                {roles.map((role) => {
                  const color = roleColors[role.name] || "#64748b";
                  return (
                    <button
                      key={role.id}
                      onClick={() => { setSelectedRole(role); setEditMode(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all cursor-pointer ${selectedRole.id === role.id ? "bg-amber-50" : "hover:bg-gray-50"}`}
                    >
                      <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: color + "18" }}>
                        <i className="ri-shield-keyhole-line text-sm" style={{ color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{role.name}</p>
                        <p className="text-xs text-gray-400">{role.userCount} user(s)</p>
                      </div>
                      {role.isSystem && (
                        <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full flex-shrink-0">System</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Permissions Matrix */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-gray-900">{selectedRole.name}</h3>
                    {selectedRole.isSystem && (
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">System Role</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{selectedRole.description}</p>
                </div>
                <div className="flex gap-2">
                  {editMode ? (
                    <>
                      <button onClick={() => setEditMode(false)} className="px-3 py-1.5 border border-gray-200 text-gray-600 rounded-xl text-xs font-semibold cursor-pointer hover:bg-gray-50 whitespace-nowrap">Cancel</button>
                      <button onClick={savePerms} className="px-3 py-1.5 text-white rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap" style={{ background: "#7c3aed" }}>Save Permissions</button>
                    </>
                  ) : (
                    <button onClick={startEdit} disabled={selectedRole.isSystem}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-white rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ background: "#7c3aed" }}>
                      <i className="ri-edit-line text-xs" />Edit Permissions
                    </button>
                  )}
                </div>
              </div>

              {/* Permission grid */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-36">Module</th>
                      {allPermissions.map((p) => (
                        <th key={p} className="text-center px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          <span className="px-2 py-0.5 rounded-full text-xs" style={{ color: permColor[p].color, background: permColor[p].bg }}>
                            {p}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {allModules.map((mod) => {
                      const currentPerms = editMode ? (editPerms[mod] || []) : (selectedRole.permissions[mod] || []);
                      return (
                        <tr key={mod} className="hover:bg-gray-50 transition-all">
                          <td className="px-5 py-3 text-sm font-medium text-gray-700 whitespace-nowrap">{mod}</td>
                          {allPermissions.map((perm) => {
                            const has = currentPerms.includes(perm);
                            return (
                              <td key={perm} className="px-3 py-3 text-center">
                                {editMode ? (
                                  <button
                                    onClick={() => togglePerm(mod, perm)}
                                    className={`w-6 h-6 rounded-lg flex items-center justify-center mx-auto cursor-pointer transition-all ${has ? "text-white" : "bg-gray-100 text-gray-300 hover:bg-gray-200"}`}
                                    style={has ? { background: permColor[perm].color } : {}}
                                  >
                                    <i className={`${has ? "ri-checkbox-circle-fill" : "ri-checkbox-blank-circle-line"} text-sm`} />
                                  </button>
                                ) : (
                                  <span className="flex items-center justify-center">
                                    {has ? (
                                      <i className="ri-checkbox-circle-fill text-base" style={{ color: permColor[perm].color }} />
                                    ) : (
                                      <i className="ri-close-circle-line text-base text-gray-200" />
                                    )}
                                  </span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {selectedRole.isSystem && (
                <div className="px-5 py-3 border-t border-gray-100 bg-amber-50">
                  <p className="text-xs text-amber-700">
                    <i className="ri-information-line mr-1" />
                    System roles cannot be modified. Clone this role to create a custom version.
                  </p>
                </div>
              )}
              {!selectedRole.isSystem && !editMode && (
                <div className="px-5 py-3 border-t border-gray-100 bg-amber-50 flex items-center justify-between gap-3">
                  <p className="text-xs text-amber-700 flex items-center gap-1.5">
                    <i className="ri-shield-check-line" />
                    Permissions are <strong>live</strong> — changes apply immediately to all users with this role.
                  </p>
                  <button
                    onClick={async () => {
                      const defaultRole = mockRoles.find((r) => r.id === selectedRole.id);
                      if (defaultRole) {
                        const updated = roles.map((r) => r.id === selectedRole.id ? { ...r, permissions: defaultRole.permissions } : r);
                        setRoles(updated);
                        setSelectedRole({ ...selectedRole, permissions: defaultRole.permissions });
                        // Save full map to DB
                        const fullMap: RolePermissionsMap = {};
                        updated.forEach((r) => { fullMap[r.name] = r.permissions; });
                        try { await savePermissions(fullMap); } catch { /* offline */ }
                      }
                      showSuccess("Reset to default permissions");
                    }}
                    className="text-xs text-amber-700 underline cursor-pointer hover:text-amber-900 whitespace-nowrap flex-shrink-0"
                  >
                    Reset to defaults
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ERPLayout>
  );
}
